import React, { useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  Chip,
  Tabs,
  Tab,
  Paper
} from '@mui/material';
import {
  Person,
  Radio,
  Event,
  Settings,
  Image,
  Dashboard as DashboardIcon,
  Info,
  Videocam,
  Download,
  ContactPhone,
  Image as ImageIcon
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import UserManagement from '../components/dashboard/UserManagement';
import ProgramManagement from '../components/dashboard/ProgramManagement';
import EventManagement from '../components/dashboard/EventManagement';
import BannerManagement from '../components/dashboard/BannerManagement';
import DownloadManagement from '../components/dashboard/DownloadManagement';
import ContactManagement from '../components/dashboard/ContactManagement';
import RadioInfoManagement from '../components/dashboard/RadioInfoManagement';
import LiveCameraManagement from '../components/dashboard/LiveCameraManagement';
import DJProfileManagement from '../components/dashboard/DJProfileManagement';

import DJProgramManagement from '../components/dashboard/DJProgramManagement';
import DJDownloads from '../components/dashboard/DJDownloads';
import DJRadioInfo from '../components/dashboard/DJRadioInfo';
import DJLiveCamera from '../components/dashboard/DJLiveCamera';
import DJChatBackgroundManagement from '../components/dashboard/DJChatBackgroundManagement';

const Dashboard = () => {
  const { user, isAdmin, isDJ, isSuperAdmin } = useAuth();
  const [activeTab, setActiveTab] = useState(0);

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return 'Bom dia';
    if (hour < 18) return 'Boa tarde';
    return 'Boa noite';
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'superadmin':
        return 'Super Administrador';
      case 'admin':
        return 'Administrador';
      case 'dj':
        return 'DJ';
      case 'organizer':
        return 'Organizador';
      default:
        return 'Usuário';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'pending':
        return 'warning';
      case 'inactive':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'active':
        return 'Ativo';
      case 'pending':
        return 'Pendente';
      case 'inactive':
        return 'Inativo';
      default:
        return 'Desconhecido';
    }
  };

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const renderTabContent = () => {
    if (isAdmin) {
      switch (activeTab) {
        case 0:
          return <UserManagement />;
        case 1:
          return <ProgramManagement />;
        case 2:
          return <EventManagement />;
        case 3:
          return <BannerManagement />;
        case 4:
          return <DownloadManagement />;
        case 5:
          return <ContactManagement />;
        case 6:
          return <RadioInfoManagement />;
        case 7:
          return <LiveCameraManagement />;
        default:
          return <UserManagement />;
      }

    } else if (isDJ) {
      switch (activeTab) {
        case 0:
          return <DJProfileManagement />;
        case 1:
          return <DJProgramManagement />;
        case 2:
          return <DJDownloads />;
        case 3:
          return <DJRadioInfo />;
        case 4:
          return <DJLiveCamera />;
        case 5:
          return <DJChatBackgroundManagement />;
        default:
          return <DJProfileManagement />;
      }
    }
    return null;
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box sx={{ mb: 4 }}>
        <Typography
          variant="h4"
          sx={{
            fontFamily: 'Orbitron',
            fontWeight: 700,
            color: '#FF6B35',
            mb: 1,
          }}
        >
          {getGreeting()}, {user?.name}!
        </Typography>
        <Typography variant="body1" sx={{ opacity: 0.7 }}>
          Bem-vindo ao seu dashboard da NostalBeat
        </Typography>
      </Box>


      {/* Status Messages */}
      {user?.status === 'pending' && (
        <Card
          sx={{
            bgcolor: 'rgba(255, 152, 0, 0.1)',
            border: '1px solid rgba(255, 152, 0, 0.3)',
            mb: 3,
          }}
        >
          <CardContent>
            <Typography variant="h6" sx={{ color: '#FF9800', mb: 1 }}>
              ⏳ Aguardando Aprovação
            </Typography>
            <Typography variant="body2" sx={{ opacity: 0.8 }}>
              Seu cadastro está sendo analisado pela administração.
              Você receberá um email quando sua conta for aprovada.
            </Typography>
          </CardContent>
        </Card>
      )}

      {/* Admin Tabs */}
      {isAdmin && (
        <Paper sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#FF6B35',
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#FF6B35',
              }
            }}
          >
            <Tab
              icon={<Person />}
              label="Usuários"
              iconPosition="start"
            />
            <Tab
              icon={<Radio />}
              label="Programas"
              iconPosition="start"
            />
            <Tab
              icon={<Event />}
              label="Eventos"
              iconPosition="start"
            />

            <Tab
              icon={<Image />}
              label="Banners"
              iconPosition="start"
            />
            <Tab
              icon={<Download />}
              label="Downloads"
              iconPosition="start"
            />
            <Tab
              icon={<ContactPhone />}
              label="Contatos"
              iconPosition="start"
            />
            <Tab
              icon={<Info />}
              label="Informações da Rádio"
              iconPosition="start"
            />
            <Tab
              icon={<Videocam />}
              label="Câmera ao Vivo"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      )}

      {/* DJ Content */}
      {isDJ && !isAdmin && (
        <Paper sx={{ bgcolor: 'rgba(10, 10, 10, 0.5)' }}>
          <Tabs
            value={activeTab}
            onChange={handleTabChange}
            variant="scrollable"
            scrollButtons="auto"
            sx={{
              '& .MuiTab-root': {
                color: 'rgba(255, 255, 255, 0.7)',
                fontWeight: 600,
                textTransform: 'none',
                minHeight: 48,
                '&.Mui-selected': {
                  color: '#FF6B35',
                },
              },
              '& .MuiTabs-indicator': {
                bgcolor: '#FF6B35',
              }
            }}
          >
            <Tab
              icon={<Person />}
              label="Meu Perfil"
              iconPosition="start"
            />
            <Tab
              icon={<Radio />}
              label="Meus Programas"
              iconPosition="start"
            />
            <Tab
              icon={<Download />}
              label="Downloads"
              iconPosition="start"
            />
            <Tab
              icon={<Info />}
              label="Informações da Rádio"
              iconPosition="start"
            />
            <Tab
              icon={<Videocam />}
              label="Câmera ao Vivo"
              iconPosition="start"
            />
            <Tab
              icon={<ImageIcon />}
              label="Background Chat"
              iconPosition="start"
            />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {renderTabContent()}
          </Box>
        </Paper>
      )}
    </Container>
  );
};

export default Dashboard;
